import { CrownIcon } from '@src/components/icons/CrownIcon';
import { useTranslation } from 'react-i18next';
import { Button, Stack, Switch, showDialog, showToast } from '@src/components';
import { PREMIUM_TIERS, type PremiumTier } from '@src/components/PremiumBadge/tierIdentity';

type HelpDeskPremiumProps = {
  userId: number;
  currentTier?: string | null;
  email?: string | null;
  onGranted: (tier: string | null) => void;
  onDenied: () => void;
};

export function HelpDeskPremium({
  userId,
  currentTier,
  email,
  onGranted,
  onDenied,
}: HelpDeskPremiumProps) {
  const { i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const label = ru ? 'Подарить Премиум' : 'Gift Premium';
  const tiers = Object.keys(PREMIUM_TIERS) as PremiumTier[];
  const included = (tier: PremiumTier) =>
    currentTier === 'full' || Boolean(currentTier?.startsWith('tier_') && currentTier >= tier);
  function confirm(tier: PremiumTier, sendEmail = false, error = false) {
    const name = PREMIUM_TIERS[tier][ru ? 'ru' : 'en'];
    showDialog({
      title: `${label} · #${userId}`,
      children: (
        <Stack gap={3}>
          <fieldset className="help-desk__premium-options">
            <legend>{ru ? 'Выберите тариф' : 'Choose a plan'}</legend>
            {tiers.map((option) => (
              <label
                key={option}
                className={`help-desk__premium-option help-desk__premium-option--${option}`}
              >
                <input
                  type="radio"
                  name="premium-tier"
                  value={option}
                  checked={tier === option}
                  onChange={() => confirm(option, sendEmail)}
                />
                <CrownIcon />
                <span>{PREMIUM_TIERS[option][ru ? 'ru' : 'en']}</span>
                {included(option) && <small>{ru ? 'Уже доступно' : 'Already included'}</small>}
              </label>
            ))}
          </fieldset>
          <p>
            {ru
              ? 'Без оплаты. Доступ добавится профилю и играм сезона 2026–2027, где пользователь — организатор. Уже доступные возможности сохранятся.'
              : 'No charge. Adds access to the profile and its organized games for the 2026–2027 season. Existing benefits are preserved.'}
          </p>
          <Switch
            checked={sendEmail}
            onCheckedChange={(value) => confirm(tier, value)}
            disabled={!email}
            label={ru ? 'Отправить письмо' : 'Send email'}
            hint={
              !email
                ? ru
                  ? 'У пользователя нет email.'
                  : 'This user has no email address.'
                : ru
                ? `Стандартное письмо об активации Премиума: ${email}`
                : `Standard Premium activation email: ${email}`
            }
          />
          {error && (
            <p role="alert">
              {ru
                ? 'Не удалось предоставить Премиум. Попробуйте ещё раз.'
                : 'Could not grant Premium. Please retry.'}
            </p>
          )}
        </Stack>
      ),
      cancel: ru ? 'Отмена' : 'Cancel',
      submit: included(tier)
        ? ru
          ? 'Уже доступно'
          : 'Already included'
        : ru
        ? `Предоставить: ${name}`
        : `Grant: ${name}`,
      submitDisabled: included(tier),
      closeLabel: ru ? 'Закрыть' : 'Close',
      testId: 'help-desk-premium-confirm',
      onSubmit: async () => {
        try {
          const response = await fetch(`/api/admin/help-desk/users/${userId}/premium`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tier, sendEmail }),
          });
          if ([401, 403].includes(response.status)) {
            onDenied();
            return;
          }
          if (!response.ok) throw new Error('Grant failed');
          const result = await response.json();
          onGranted(result.premiumTier);
          if (result.emailFailed)
            showToast(
              ru
                ? 'Премиум предоставлен, но письмо не отправлено.'
                : 'Premium granted, but the email could not be sent.',
            );
          else
            showToast(
              ru
                ? result.emailSent
                  ? 'Премиум предоставлен. Письмо отправлено.'
                  : 'Премиум предоставлен.'
                : result.emailSent
                ? 'Premium granted. Email sent.'
                : 'Premium granted.',
              'success',
            );
        } catch (error) {
          confirm(tier, sendEmail, true);
          throw error;
        }
      },
    });
  }
  return (
    <Button
      variant="ghost"
      className="help-desk__action-button help-desk__action-button--premium"
      icon={<CrownIcon />}
      aria-label={label}
      title={label}
      onClick={() => confirm(tiers.find((tier) => !included(tier)) ?? 'tier_1')}
    />
  );
}
